/**
 * security/SecurityContext.ts — centralized auth/permission state for Roamer Core™.
 *
 * One place holds authentication state, validates the session, checks
 * permissions, and (in the future) refreshes tokens. Engines ask the core —
 * they never carry their own auth logic. This is architecture + a working
 * capability model today; real token flows plug in behind the same surface
 * when a backend auth provider is wired.
 */

export interface SessionInfo {
  sessionId: string;
  userId: string;
  /** Epoch ms after which the session must be revalidated/refreshed. */
  expiresAt: number;
  permissions: string[];
}

export type TokenRefreshHandler = (session: SessionInfo) => Promise<SessionInfo | null>;

export class SecurityContext {
  private session: SessionInfo | null = null;
  private refreshHandler: TokenRefreshHandler | null = null;

  /** Establish the authenticated session (called by the future auth flow). */
  setSession(session: SessionInfo | null): void {
    this.session = session ? { ...session, permissions: [...session.permissions] } : null;
  }

  getSession(): SessionInfo | null {
    return this.session ? { ...this.session, permissions: [...this.session.permissions] } : null;
  }

  isAuthenticated(now: number): boolean {
    return this.session != null && this.session.expiresAt > now;
  }

  /** Session validation — expired sessions are treated as absent. */
  validateSession(now: number): boolean {
    if (!this.session) return false;
    if (this.session.expiresAt <= now) {
      this.session = null;
      return false;
    }
    return true;
  }

  /** Permission check: '*' grants everything; otherwise exact match. */
  hasPermission(permission: string, now: number): boolean {
    if (!this.validateSession(now)) return false;
    const perms = this.session!.permissions;
    return perms.includes('*') || perms.includes(permission);
  }

  /** Plug in the (future) token refresh flow. */
  onTokenRefresh(handler: TokenRefreshHandler): void {
    this.refreshHandler = handler;
  }

  /** Refresh the session if it is close to expiry. Fails closed. */
  async refreshIfNeeded(now: number, windowMs = 5 * 60_000): Promise<boolean> {
    if (!this.session) return false;
    if (this.session.expiresAt - now > windowMs) return true;
    if (!this.refreshHandler) return this.validateSession(now);
    try {
      const refreshed = await this.refreshHandler(this.session);
      this.session = refreshed;
      return refreshed != null;
    } catch {
      this.session = null;
      return false;
    }
  }
}
